// =====================================================
// 白板协作 - CollabManager
// Supabase Realtime Broadcast + Presence
// =====================================================

import { CaptureUpdateAction, getSceneVersion, reconcileElements } from '@excalidraw/excalidraw';
import type { RemoteExcalidrawElement } from '@excalidraw/excalidraw/data/reconcile';
import type { OrderedExcalidrawElement } from '@excalidraw/excalidraw/element/types';
import type { Collaborator, ExcalidrawImperativeAPI, SocketId } from '@excalidraw/excalidraw/types';
import type { RealtimeChannel } from '@supabase/supabase-js';
import throttle from 'lodash.throttle';
import { COLLAB_COLORS } from '../types';
import type { CollabConnectionStatus, CollabUser, CollaborationEndedReason } from '../types';
import { supabase } from '@/config/supabase';

const CURSOR_SYNC_TIMEOUT = 33;
const ELEMENT_SYNC_TIMEOUT = 100;
const BROADCAST_RETRIES = 2;

type ScenePayload = {
  senderId: string;
  elements: any[];
  targetId?: string;
};

interface SceneRequestPayload {
  senderId: string;
  requestId: string;
}

interface CollaborationEndedPayload {
  senderId: string;
  ownerId?: string;
}

interface CollaboratorRemovedPayload {
  senderId: string;
  targetUserId: string;
}

export interface CollabManagerOptions {
  projectId: string;
  excalidrawAPI: ExcalidrawImperativeAPI;
  currentUser: {
    userId: string;
    username: string;
    email: string;
    avatarUrl?: string;
  };
  ownerId?: string;
  onCollaboratorsChange?: (collaborators: Map<string, CollabUser>) => void;
  onConnectionChange?: (status: CollabConnectionStatus, error?: string) => void;
  /** 远端场景已写入 Excalidraw；编辑器应将合并后的结果纳入唯一保存队列。 */
  onRemoteSceneApplied?: () => void;
  /** WebSocket 重连成功后，从数据库补一次持久化快照。 */
  onConnectionRecovered?: (isReconnect: boolean) => Promise<void>;
  /** 所有者关闭协作时通知远端客户端退出。 */
  onCollaborationEnded?: (reason: CollaborationEndedReason) => void;
  /** 从 Storage 补齐远端场景中本地不存在的图片文件。 */
  onFilesNeeded?: (imageElements: any[]) => Promise<Record<string, any>>;
}

/**
 * 只负责 Realtime 会话与内存场景合并；数据库持久化由编辑器统一处理。
 * 这样避免 Broadcast 线程和编辑器自动保存同时覆盖整份 canvas_data。
 */
export class CollabManager {
  private channel: RealtimeChannel | null = null;
  private readonly excalidrawAPI: ExcalidrawImperativeAPI;
  private readonly projectId: string;
  private readonly currentUser: CollabManagerOptions['currentUser'];
  private readonly ownerId?: string;
  private readonly onCollaboratorsChange?: CollabManagerOptions['onCollaboratorsChange'];
  private readonly onConnectionChange?: CollabManagerOptions['onConnectionChange'];
  private readonly onRemoteSceneApplied?: CollabManagerOptions['onRemoteSceneApplied'];
  private readonly onConnectionRecovered?: CollabManagerOptions['onConnectionRecovered'];
  private readonly onCollaborationEnded?: CollabManagerOptions['onCollaborationEnded'];
  private readonly onFilesNeeded?: CollabManagerOptions['onFilesNeeded'];

  private collaborators = new Map<string, CollabUser>();
  private connectionStatus: CollabConnectionStatus = 'disconnected';
  private isDestroyed = false;
  private hasConnected = false;
  private latestLocalElements: readonly OrderedExcalidrawElement[] | null = null;
  private lastBroadcastedSceneVersion = -1;
  private lastAppliedRemoteSceneVersion = -1;
  private applyingRemoteScene = false;
  private remoteSceneQueue: Promise<void> = Promise.resolve();

  constructor(options: CollabManagerOptions) {
    this.projectId = options.projectId;
    this.excalidrawAPI = options.excalidrawAPI;
    this.currentUser = options.currentUser;
    this.ownerId = options.ownerId;
    this.onCollaboratorsChange = options.onCollaboratorsChange;
    this.onConnectionChange = options.onConnectionChange;
    this.onRemoteSceneApplied = options.onRemoteSceneApplied;
    this.onConnectionRecovered = options.onConnectionRecovered;
    this.onCollaborationEnded = options.onCollaborationEnded;
    this.onFilesNeeded = options.onFilesNeeded;
  }

  async start(): Promise<void> {
    if (this.channel) return;

    this.isDestroyed = false;
    this.setConnectionStatus('connecting');

    const channel = supabase.channel(`wb-collab:${this.projectId}`, {
      config: {
        private: true,
        broadcast: { self: false, ack: true },
        presence: { key: this.currentUser.userId },
      },
    });
    this.channel = channel;

    channel
      .on('broadcast', { event: 'scene-update' }, (payload) => {
        if (!this.isDestroyed) this.enqueueRemoteScene(payload.payload as ScenePayload);
      })
      .on('broadcast', { event: 'scene-request' }, (payload) => {
        if (!this.isDestroyed) void this.respondWithSceneSnapshot(payload.payload as SceneRequestPayload);
      })
      .on('broadcast', { event: 'scene-snapshot' }, (payload) => {
        if (!this.isDestroyed) this.enqueueRemoteScene(payload.payload as ScenePayload);
      })
      .on('broadcast', { event: 'collab-ended' }, (payload) => {
        if (!this.isDestroyed) void this.handleCollaborationEnded(payload.payload as CollaborationEndedPayload);
      })
      .on('broadcast', { event: 'collaborator-removed' }, (payload) => {
        if (!this.isDestroyed) void this.handleCollaboratorRemoved(payload.payload as CollaboratorRemovedPayload);
      })
      .on('broadcast', { event: 'cursor-move' }, (payload) => {
        if (!this.isDestroyed) this.handleRemoteCursor(payload.payload as any);
      })
      .on('presence', { event: 'sync' }, () => {
        if (!this.isDestroyed) this.handlePresenceSync();
      })
      .on('presence', { event: 'join' }, () => {
        if (!this.isDestroyed) this.handlePresenceSync();
      })
      .on('presence', { event: 'leave' }, () => {
        if (!this.isDestroyed) this.handlePresenceSync();
      });

    await new Promise<void>((resolve, reject) => {
      let settled = false;

      channel.subscribe(async (status, error) => {
        if (this.isDestroyed) return;

        if (status === 'SUBSCRIBED') {
          const isReconnect = this.hasConnected;
          this.hasConnected = true;
          this.setConnectionStatus('connected');

          try {
            await channel.track({
              userId: this.currentUser.userId,
              username: this.currentUser.username,
              email: this.currentUser.email,
              avatarUrl: this.currentUser.avatarUrl || null,
              color: this.getUserColor(this.currentUser.userId),
              joinedAt: new Date().toISOString(),
            });

            if (isReconnect && this.onConnectionRecovered) {
              this.applyingRemoteScene = true;
              try {
                await this.onConnectionRecovered(true);
              } catch (recoveryError) {
                console.warn('[Collab] 数据库重连快照加载失败，将继续请求在线用户快照:', recoveryError);
              } finally {
                queueMicrotask(() => { this.applyingRemoteScene = false; });
              }
            }
            void this.requestSceneSnapshot();
            this.replayLatestLocalScene();

            if (!settled) {
              settled = true;
              resolve();
            }
          } catch (trackError) {
            const message = trackError instanceof Error ? trackError.message : '无法注册协作在线状态';
            this.setConnectionStatus('error', message);
            if (!settled) {
              settled = true;
              reject(new Error(message));
            }
          }
          return;
        }

        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          const message = error?.message || (status === 'TIMED_OUT' ? '协作连接超时' : '协作连接失败');
          this.setConnectionStatus(this.hasConnected ? 'reconnecting' : 'error', message);
          if (!settled && !this.hasConnected) {
            settled = true;
            reject(new Error(message));
          }
          return;
        }

        if (status === 'CLOSED') {
          this.setConnectionStatus('disconnected', error?.message);
          if (!settled && !this.hasConnected) {
            settled = true;
            reject(new Error(error?.message || '协作连接已关闭'));
          }
        }
      });
    });
  }

  async stop(options: { notifyPeers?: boolean } = {}): Promise<void> {
    const channel = this.channel;
    if (!channel && this.isDestroyed) return;

    if (options.notifyPeers && channel && this.connectionStatus === 'connected') {
      await this.sendBroadcast('collab-ended', {
        senderId: this.currentUser.userId,
        ownerId: this.ownerId || this.currentUser.userId,
      });
    }

    this.isDestroyed = true;
    this.broadcastElements.cancel();
    this.broadcastCursor.cancel();
    this.channel = null;

    if (channel) {
      try {
        await channel.untrack();
      } finally {
        await supabase.removeChannel(channel);
      }
    }

    this.collaborators.clear();
    this.lastBroadcastedSceneVersion = -1;
    this.lastAppliedRemoteSceneVersion = -1;
    this.setConnectionStatus('disconnected');
    this.onCollaboratorsChange?.(new Map());
    this.excalidrawAPI.updateScene({ collaborators: new Map() });
  }

  async notifyCollaborationEnded(): Promise<void> {
    if (this.ownerId && this.ownerId !== this.currentUser.userId) return;
    await this.sendBroadcast('collab-ended', {
      senderId: this.currentUser.userId,
      ownerId: this.ownerId || this.currentUser.userId,
    });
  }

  async notifyCollaboratorRemoved(targetUserId: string): Promise<void> {
    if (this.ownerId && this.ownerId !== this.currentUser.userId) return;
    await this.sendBroadcast('collaborator-removed', {
      senderId: this.currentUser.userId,
      targetUserId,
    });
  }

  destroy(): void {
    void this.stop();
  }

  syncElements = (elements: readonly OrderedExcalidrawElement[]): void => {
    if (this.isDestroyed || this.applyingRemoteScene) return;

    this.latestLocalElements = elements;
    const currentVersion = getSceneVersion(elements);
    if (currentVersion === this.lastBroadcastedSceneVersion) return;
    this.broadcastElements(elements);
  };

  private broadcastElements = throttle(
    (elements: readonly OrderedExcalidrawElement[]) => {
      void this.broadcastScene(elements);
    },
    ELEMENT_SYNC_TIMEOUT,
    { leading: false, trailing: true },
  );

  private async broadcastScene(elements: readonly OrderedExcalidrawElement[]): Promise<void> {
    if (this.isDestroyed || this.connectionStatus !== 'connected') return;

    const sceneVersion = getSceneVersion(elements);
    if (sceneVersion === this.lastBroadcastedSceneVersion) return;

    const ok = await this.sendBroadcast('scene-update', {
      senderId: this.currentUser.userId,
      elements: this.getSyncableElements(elements),
    });

    if (ok) this.lastBroadcastedSceneVersion = sceneVersion;
  }

  private replayLatestLocalScene(): void {
    if (!this.latestLocalElements) return;
    if (getSceneVersion(this.latestLocalElements) === this.lastBroadcastedSceneVersion) return;
    this.broadcastElements(this.latestLocalElements);
  }

  private async requestSceneSnapshot(): Promise<void> {
    await this.sendBroadcast('scene-request', {
      senderId: this.currentUser.userId,
      requestId: crypto.randomUUID(),
    });
  }

  private async respondWithSceneSnapshot(data: SceneRequestPayload): Promise<void> {
    if (data.senderId === this.currentUser.userId || !data.requestId) return;

    const elements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
    await this.sendBroadcast('scene-snapshot', {
      senderId: this.currentUser.userId,
      targetId: data.senderId,
      elements: this.getSyncableElements(elements),
    });
  }

  private enqueueRemoteScene(data: ScenePayload): void {
    if (!data?.senderId || data.senderId === this.currentUser.userId) return;
    if (data.targetId && data.targetId !== this.currentUser.userId) return;

    this.remoteSceneQueue = this.remoteSceneQueue
      .then(() => this.applyRemoteScene(data))
      .catch((error) => console.error('[Collab] 应用远端场景失败:', error));
  }

  private async applyRemoteScene(data: ScenePayload): Promise<void> {
    const localElements = this.excalidrawAPI.getSceneElementsIncludingDeleted();
    const remoteElements = (data.elements || []) as readonly RemoteExcalidrawElement[];
    const reconciledElements = reconcileElements(
      localElements,
      remoteElements,
      this.excalidrawAPI.getAppState(),
    );

    const nextSceneVersion = getSceneVersion(reconciledElements);
    const hasSceneChange = nextSceneVersion !== this.lastAppliedRemoteSceneVersion;
    this.lastAppliedRemoteSceneVersion = nextSceneVersion;

    this.applyingRemoteScene = true;
    try {
      this.excalidrawAPI.updateScene({
        elements: reconciledElements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    } finally {
      queueMicrotask(() => {
        this.applyingRemoteScene = false;
      });
    }

    await this.loadMissingFiles(remoteElements);
    if (hasSceneChange) this.onRemoteSceneApplied?.();
  }

  private async loadMissingFiles(remoteElements: readonly RemoteExcalidrawElement[]): Promise<void> {
    if (!this.onFilesNeeded) return;

    const localFiles = this.excalidrawAPI.getFiles();
    const missingImageElements = remoteElements.filter((element: any) => (
      element.type === 'image' && element.fileId && !localFiles[element.fileId]
    ));
    if (!missingImageElements.length) return;

    const files = await this.onFilesNeeded(missingImageElements as any[]);
    if (Object.keys(files).length > 0) {
      this.excalidrawAPI.addFiles(Object.values(files));
    }
  }

  onPointerUpdate = (payload: {
    pointer: { x: number; y: number; tool: 'pointer' | 'laser' };
    button: 'down' | 'up';
    pointersMap: Map<number, any>;
  }): void => {
    if (payload.pointersMap.size < 2) this.broadcastCursor(payload);
  };

  private broadcastCursor = throttle(
    (payload: {
      pointer: { x: number; y: number; tool: 'pointer' | 'laser' };
      button: 'down' | 'up';
    }) => {
      if (this.isDestroyed || this.connectionStatus !== 'connected') return;
      void this.sendBroadcast('cursor-move', {
        senderId: this.currentUser.userId,
        pointer: payload.pointer,
        button: payload.button,
        selectedElementIds: this.excalidrawAPI.getAppState().selectedElementIds,
      });
    },
    CURSOR_SYNC_TIMEOUT,
    { leading: true, trailing: true },
  );

  private handleRemoteCursor(data: {
    senderId?: string;
    /** 兼容已有客户端在旧协议中发送的错误字段。 */
    odI?: string;
    pointer: { x: number; y: number; tool: 'pointer' | 'laser' };
    button: 'down' | 'up';
    selectedElementIds?: Record<string, boolean>;
  }): void {
    const senderId = data.senderId || data.odI;
    if (!senderId || senderId === this.currentUser.userId) return;

    const collaborator = this.collaborators.get(senderId);
    if (collaborator) {
      collaborator.pointer = data.pointer;
      collaborator.button = data.button;
      collaborator.selectedElementIds = data.selectedElementIds;
      this.updateExcalidrawCollaborators();
    }
  }

  private async handleCollaborationEnded(data: CollaborationEndedPayload): Promise<void> {
    if (data.senderId === this.currentUser.userId) return;
    if (this.ownerId && data.ownerId && data.ownerId !== this.ownerId) return;

    await this.stop();
    this.onCollaborationEnded?.('closed');
  }

  private async handleCollaboratorRemoved(data: CollaboratorRemovedPayload): Promise<void> {
    if (data.senderId === this.currentUser.userId) return;
    if (data.targetUserId !== this.currentUser.userId) return;
    await this.stop();
    this.onCollaborationEnded?.('removed');
  }

  private handlePresenceSync(): void {
    const channel = this.channel;
    if (!channel) return;

    const newCollaborators = new Map<string, CollabUser>();
    for (const presences of Object.values(channel.presenceState())) {
      for (const presence of presences as any[]) {
        const userId = presence.userId as string;
        if (!userId) continue;
        const existing = this.collaborators.get(userId);
        newCollaborators.set(userId, {
          userId,
          username: presence.username || '匿名',
          email: (presence.email as string) || '',
          avatarUrl: presence.avatarUrl || undefined,
          color: (presence.color as string) || existing?.color || this.getUserColor(userId),
          pointer: existing?.pointer,
          button: existing?.button,
          isCurrentUser: userId === this.currentUser.userId,
          idleState: existing?.idleState || 'active',
          selectedElementIds: existing?.selectedElementIds,
        });
      }
    }

    this.collaborators = newCollaborators;
    this.onCollaboratorsChange?.(new Map(newCollaborators));
    this.updateExcalidrawCollaborators();
  }

  private updateExcalidrawCollaborators(): void {
    const excalidrawCollaborators = new Map<SocketId, Collaborator>();
    for (const [userId, user] of this.collaborators) {
      const socketId = userId as unknown as SocketId;
      excalidrawCollaborators.set(socketId, {
        pointer: user.isCurrentUser ? undefined : user.pointer,
        button: user.isCurrentUser ? undefined : user.button || 'up',
        selectedElementIds: user.isCurrentUser ? undefined : user.selectedElementIds || {},
        username: user.username,
        color: { background: user.color, stroke: user.color },
        avatarUrl: user.avatarUrl || '',
        userState: user.idleState === 'active' ? undefined : (user.idleState as any),
        isCurrentUser: user.isCurrentUser,
      } as Collaborator);
    }
    this.excalidrawAPI.updateScene({ collaborators: excalidrawCollaborators });
  }

  private getSyncableElements(elements: readonly any[]): any[] {
    // 删除元素也是 reconcile 所需的 tombstone，否则离线客户端可能在重连后
    // 把已删除图形重新带回来。
    return [...elements];
  }

  private getUserColor(userId: string): string {
    let hash = 0;
    for (let index = 0; index < userId.length; index += 1) {
      hash = ((hash << 5) - hash + userId.charCodeAt(index)) | 0;
    }
    return COLLAB_COLORS[Math.abs(hash) % COLLAB_COLORS.length];
  }

  private setConnectionStatus(status: CollabConnectionStatus, error?: string): void {
    if (this.connectionStatus === status && !error) return;
    this.connectionStatus = status;
    this.onConnectionChange?.(status, error);
  }

  private async sendBroadcast(event: string, payload: Record<string, any>): Promise<boolean> {
    const channel = this.channel;
    if (!channel || this.isDestroyed || this.connectionStatus !== 'connected') return false;

    for (let attempt = 0; attempt <= BROADCAST_RETRIES; attempt += 1) {
      try {
        const result: any = await channel.send({ type: 'broadcast', event, payload });
        if (result === 'ok' || result?.status === 'ok') return true;
      } catch (error) {
        if (attempt === BROADCAST_RETRIES) {
          console.warn('[Collab] Broadcast 发送失败:', event, error);
        }
      }
    }
    return false;
  }

  getCollaborators(): Map<string, CollabUser> {
    return new Map(this.collaborators);
  }

  getConnectionStatus(): CollabConnectionStatus {
    return this.connectionStatus;
  }

  isApplyingRemoteScene(): boolean {
    return this.applyingRemoteScene;
  }
}
